import { useData } from "../../runtime/provider";
import loadingAnimation from "@assets/animations/loading-no-background.json";
import LBox from "@components/LBox";
import {
  createScannerSession,
  type ScanResult,
} from "@utils/ticketScanner/session";
import { randomUUID } from "expo-crypto";
import { auth } from "@utils/firebase";
import { captureException } from "@utils/captureException";
import {
  BottomSheetActionLayout,
  BottomSheetPlainLayout,
  BottomSheetSubmitButton,
  BottomSheetSubtitle,
  BottomSheetTitle,
} from "@utils/swmModal/components/BottomSheetPrimitives";

import { CameraView, useCameraPermissions } from "expo-camera";
import Lottie from "lottie-react-native";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { Dimensions } from "react-native";
import { type SheetAdapterRef } from "react-native-bottom-sheet-stack";

import AppModalSheet from "@utils/appModal/components/AppModalSheet";

type ScannedTicket = {
  redeemCode: string;
};

const CAMERA_HEIGHT = Math.min(Dimensions.get("window").height * 0.48, 420);

const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split("@");
  if (!localPart || !domain) return "***@***.***";

  if (localPart.length <= 2) {
    return `${localPart[0] ?? "*"}***@${domain}`;
  }

  const firstChar = localPart[0];
  const lastChar = localPart[localPart.length - 1];
  return `${firstChar}*****${lastChar}@${domain}`;
};

/**
 * What a ticket QR says. The engine's QR (the one in the confirmation email,
 * ADR-0015) is the bare admission code; the legacy view-tickets page encodes
 * `{"redeemCode": …}`. Both are the same credential at the door.
 */
const parseScannedTicket = (rawData: string): ScannedTicket | undefined => {
  try {
    const parsed = JSON.parse(rawData);

    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "redeemCode" in parsed &&
      typeof parsed.redeemCode === "string" &&
      parsed.redeemCode.length > 0
    ) {
      return { redeemCode: parsed.redeemCode };
    }
    return undefined;
  } catch {
    const bare = rawData.trim();
    // A code, not a URL or a sentence: one token of a credential's shape.
    if (/^[A-Za-z0-9_-]{6,100}$/.test(bare)) return { redeemCode: bare };
    return undefined;
  }
};

const Loading = () => (
  <BottomSheetPlainLayout
    contentProps={{ alignItems: "center", justifyContent: "center" }}
  >
    <Lottie
      autoPlay
      loop
      source={loadingAnimation}
      style={{ width: 100, height: 100 }}
    />
  </BottomSheetPlainLayout>
);

const ScanError = ({
  message,
  onTryAgain,
}: {
  message: string;
  onTryAgain: () => void;
}) => (
  <BottomSheetActionLayout
    iconType="error"
    title="Invalid ticket"
    subtitle={message}
    actions={
      <BottomSheetSubmitButton
        label="TRY AGAIN"
        onPress={onTryAgain}
        sentryEventName="scan_modal_try_again"
      />
    }
  />
);

type ScanModalSWMProps = {
  closeModal: () => void;
  accessId: string;
};

const ScanModalSWM = ({ closeModal, accessId }: ScanModalSWMProps) => {
  const data = useData();
  const [permission, requestPermission] = useCameraPermissions();
  const sessionRef = useRef<ReturnType<typeof createScannerSession> | null>(
    null,
  );
  const [ready, setReady] = useState(false);
  const [openAttempt, setOpenAttempt] = useState(0);
  const claimStartedRef = useRef(false);
  const scanLockedRef = useRef(false);
  const [scanned, setScanned] = useState<ScannedTicket | undefined>();
  const [claimResult, setClaimResult] = useState<ScanResult | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const resetScanner = useCallback(() => {
    claimStartedRef.current = false;
    scanLockedRef.current = false;
    setScanned(undefined);
    setClaimResult(undefined);
    setErrorMessage(undefined);
    if (!ready) setOpenAttempt((attempt) => attempt + 1);
  }, [ready]);

  useEffect(() => {
    let active = true;
    let accountChanged = false;
    const session = createScannerSession({
      openDoor: (idempotencyKey) =>
        data.api.user.accesses.openDoor.mutate({
          accessId,
          idempotencyKey,
        }),
      claim: (redeemCode, idempotencyKey) =>
        data.api.user.accesses.claimTicket.mutate({
          redeemCode,
          idempotencyKey,
        }),
      newScanId: randomUUID,
    });
    sessionRef.current = session;
    const initialUid = auth.currentUser?.uid;
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user?.uid !== initialUid) {
        accountChanged = true;
        session.dispose();
        if (active) {
          setReady(false);
          setErrorMessage(
            "Your account changed. Close and reopen the scanner.",
          );
        }
      }
    });
    void session
      .open()
      .then(() => {
        if (active && !accountChanged) setReady(true);
      })
      .catch((error: Error) => {
        if (active && !accountChanged) setErrorMessage(error.message);
      });
    return () => {
      active = false;
      session.dispose();
      sessionRef.current = null;
      unsubscribe();
    };
  }, [accessId, openAttempt]);

  useEffect(() => {
    if (!scanned || !ready || claimStartedRef.current) return;
    claimStartedRef.current = true;
    let active = true;
    const session = sessionRef.current;
    if (!session) return;
    void session
      .scan(scanned.redeemCode)
      .then((result) => {
        if (active) setClaimResult(result);
      })
      .catch((error: Error) => {
        if (active) setErrorMessage(error.message);
      });
    return () => {
      active = false;
    };
  }, [ready, scanned]);

  const handleBarcodeScanned = useCallback((event: { data: string }) => {
    if (scanLockedRef.current) return;

    scanLockedRef.current = true;
    const nextTicket = parseScannedTicket(event.data);

    if (!nextTicket) {
      setErrorMessage("This QR code does not look like a Lissen ticket.");
      return;
    }

    setScanned(nextTicket);
  }, []);

  const requestCameraPermission = useCallback(() => {
    requestPermission().catch((error) => {
      captureException(error, {
        channel: "breadcrumb",
        area: "permissions",
        operation: "request-camera-permission",
      });
    });
  }, [requestPermission]);

  if (errorMessage) {
    return <ScanError message={errorMessage} onTryAgain={resetScanner} />;
  }

  if (!permission || !ready) {
    return <Loading />;
  }

  if (!permission.granted) {
    return (
      <BottomSheetActionLayout
        iconType="warning"
        title="Camera access"
        subtitle="We need your permission to access your camera."
        actions={
          <LBox gap="s3">
            <BottomSheetSubmitButton
              label="ALLOW"
              onPress={requestCameraPermission}
              sentryEventName="scan_modal_allow_camera_permission_swm"
            />
          </LBox>
        }
      />
    );
  }

  if (!scanned) {
    return (
      <BottomSheetPlainLayout contentProps={{ gap: "l1", px: "m1" }}>
        <LBox gap="s2">
          <BottomSheetTitle textAlign="left">Scan tickets</BottomSheetTitle>
          <BottomSheetSubtitle textAlign="left">
            Position the QR code within the frame to scan a ticket.
          </BottomSheetSubtitle>
        </LBox>
        <LBox borderRadius="xl" height={CAMERA_HEIGHT} overflow="hidden">
          <CameraView
            facing="back"
            onBarcodeScanned={handleBarcodeScanned}
            style={{
              height: "100%",
              width: "100%",
            }}
          >
            <LBox flex={1} alignItems="center" justifyContent="center">
              <LBox
                borderColor="homeGreeting"
                borderRadius="l"
                borderWidth={2}
                height={200}
                opacity={0.5}
                width={200}
              />
            </LBox>
          </CameraView>
        </LBox>
      </BottomSheetPlainLayout>
    );
  }

  if (!claimResult) {
    return <Loading />;
  }

  const ticketHolderText = claimResult.holder
    ? claimResult.holder.includes("@")
      ? maskEmail(claimResult.holder)
      : claimResult.holder
    : "Anonymous ticket holder";
  const resultTitle = claimResult.scannedBefore
    ? "Ticket already scanned"
    : "Valid ticket";

  return (
    <BottomSheetActionLayout
      iconType={claimResult.scannedBefore ? "warning" : "success"}
      title={resultTitle}
      titleProps={claimResult.scannedBefore ? { color: "debug" } : undefined}
      subtitle={ticketHolderText}
      actions={
        <LBox gap="s3">
          <BottomSheetSubmitButton
            label="SCAN ANOTHER TICKET"
            onPress={() => {
              resetScanner();
            }}
            sentryEventName="scan_modal_scan_another_ticket_swm"
          />
          <BottomSheetSubmitButton
            label="DONE"
            onPress={() => {
              sessionRef.current?.dispose();
              closeModal();
            }}
            sentryEventName="scan_modal_done_swm"
          />
        </LBox>
      }
    />
  );
};

export const ScanModalSWMSheet = forwardRef<
  SheetAdapterRef,
  { accessId: string }
>(function ScanModalSWMSheet({ accessId }, ref) {
  return (
    <AppModalSheet ref={ref} showHandle={false}>
      {({ closeModal }) => (
        <ScanModalSWM
          key={accessId}
          accessId={accessId}
          closeModal={closeModal}
        />
      )}
    </AppModalSheet>
  );
});

export default ScanModalSWM;
